import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ImageUpload from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Link2, MousePointerClick, LayoutTemplate, Activity, Plus, Copy, Trash2, Pencil, ExternalLink, Loader2 } from "lucide-react";

const emptyLink = { target_url: "", custom_alias: "", title: "", description: "", image_url: "", og_title: "", og_description: "", og_image: "", social: {}, is_active: true, use_countdown: true };

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyLink);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [siteDialog, setSiteDialog] = useState(false);
  const [newSite, setNewSite] = useState({ title: "", mode: "template" });

  useEffect(() => { document.title = "Panelim — kisalink.net"; }, []);

  const load = useCallback(async () => {
    try {
      const [s, l, m] = await Promise.all([
        api.get("/links/stats/overview"),
        api.get("/links"),
        api.get("/minisites"),
      ]);
      setStats(s.data); setLinks(l.data); setSites(m.data);
    } catch (e) {
      toast.error("Veriler yüklenemedi");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyLink); setEditId(null); setDialogOpen(true); };
  const openEdit = (l) => { setForm({ ...emptyLink, ...l, custom_alias: "" }); setEditId(l.id); setDialogOpen(true); };

  const saveLink = async () => {
    if (!form.target_url.trim()) return toast.error("Hedef URL gerekli");
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/links/${editId}`, form);
        toast.success("Link güncellendi");
      } else {
        await api.post("/links", form);
        toast.success("Link oluşturuldu");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/links/${id}`); toast.success("Silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const toggleActive = async (l) => {
    try { await api.put(`/links/${l.id}`, { is_active: !l.is_active }); load(); }
    catch { toast.error("Güncellenemedi"); }
  };

  const shortUrl = (code) => `${window.location.origin}/${code}`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Kopyalandı!"); };

  const createSite = async () => {
    if (!newSite.title.trim()) return toast.error("Başlık gerekli");
    try {
      const { data } = await api.post("/minisites", { title: newSite.title, mode: newSite.mode });
      setSiteDialog(false);
      setNewSite({ title: "", mode: "template" });
      navigate(`/builder/${data.id}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const cards = stats ? [
    { label: "Toplam Tıklanma", value: stats.total_clicks, icon: MousePointerClick, color: "text-blue-600 bg-blue-50" },
    { label: "Aktif Linkler", value: stats.active_links, icon: Link2, color: "text-emerald-600 bg-emerald-50" },
    { label: "Toplam Link", value: stats.total_links, icon: Activity, color: "text-indigo-600 bg-indigo-50" },
    { label: "Mini Siteler", value: stats.total_sites, icon: LayoutTemplate, color: "text-amber-600 bg-amber-50" },
  ] : [];

  if (loading) return (<div className="min-h-screen"><Navbar /><div className="flex items-center justify-center py-32"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div></div>);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-display">Merhaba, {user?.name} 👋</h1>
            <p className="text-slate-500 mt-1">Linklerinizi ve mini sitelerinizi buradan yönetin.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-link-button"><Plus className="w-4 h-4 mr-1" /> Link Oluştur</Button>
            <Button variant="outline" onClick={() => setSiteDialog(true)} data-testid="create-minisite-button"><LayoutTemplate className="w-4 h-4 mr-1" /> Mini Site</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-7">
          {cards.map((c, i) => (
            <Card key={i} className="p-5 rounded-2xl border-slate-200" data-testid={`stat-card-${i}`}>
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.color}`}><c.icon className="w-5 h-5" /></span>
              <p className="mt-3 text-2xl font-extrabold font-display">{c.value}</p>
              <p className="text-sm text-slate-500">{c.label}</p>
            </Card>
          ))}
        </div>

        <Card className="mt-6 p-5 rounded-2xl border-slate-200">
          <h2 className="font-semibold mb-4">Son 14 Günlük Tıklanma</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.series || []}>
                <defs>
                  <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} width={28} />
                <Tooltip />
                <Area type="monotone" dataKey="clicks" stroke="#2563EB" strokeWidth={2} fill="url(#cg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Tabs defaultValue="links" className="mt-6">
          <TabsList>
            <TabsTrigger value="links" data-testid="tab-links">Linklerim ({links.length})</TabsTrigger>
            <TabsTrigger value="sites" data-testid="tab-sites">Mini Siteler ({sites.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="links">
            <Card className="rounded-2xl border-slate-200 overflow-hidden">
              {links.length === 0 ? (
                <div className="p-12 text-center text-slate-400">Henüz link oluşturmadınız.</div>
              ) : (
                <div className="divide-y divide-slate-100" data-testid="links-list">
                  {links.map((l) => (
                    <div key={l.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`link-row-${l.code}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <a href={shortUrl(l.code)} target="_blank" rel="noreferrer" className="font-mono font-semibold text-blue-700">/{l.code}</a>
                          {l.is_active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Aktif</Badge> : <Badge variant="secondary">Pasif</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 truncate mt-0.5">{l.title || l.target_url}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center px-2"><p className="text-lg font-bold">{l.clicks}</p><p className="text-[11px] text-slate-400 uppercase">tık</p></div>
                        <Switch checked={l.is_active} onCheckedChange={() => toggleActive(l)} data-testid={`toggle-${l.code}`} />
                        <Button variant="ghost" size="icon" onClick={() => copy(shortUrl(l.code))} data-testid={`copy-${l.code}`}><Copy className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(l)} data-testid={`edit-${l.code}`}><Pencil className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-red-500" data-testid={`delete-${l.code}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Link silinsin mi?</AlertDialogTitle><AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(l.id)}>Sil</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="sites">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sites-list">
              {sites.length === 0 && <div className="col-span-full p-12 text-center text-slate-400">Henüz mini site yok.</div>}
              {sites.map((s) => (
                <Card key={s.id} className="p-5 rounded-2xl border-slate-200" data-testid={`site-card-${s.slug}`}>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="uppercase text-[10px]">{s.mode === "html" ? "HTML" : "Şablon"}</Badge>
                    {s.is_active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Yayında</Badge> : <Badge variant="secondary">Pasif</Badge>}
                  </div>
                  <h3 className="mt-3 font-semibold truncate">{s.title}</h3>
                  <p className="text-sm text-slate-500 font-mono truncate">/{s.slug}</p>
                  <p className="text-xs text-slate-400 mt-1">{s.views} görüntülenme</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 flex-1" onClick={() => navigate(`/builder/${s.id}`)} data-testid={`edit-site-${s.slug}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle</Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(`/${s.slug}`, "_blank")}><ExternalLink className="w-3.5 h-3.5" /></Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Link create/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto kl-scroll">
          <DialogHeader><DialogTitle>{editId ? "Linki Düzenle" : "Yeni Kısa Link"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">Hedef URL *</Label><Input data-testid="link-target-input" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://..." /></div>
            {!editId && <div><Label className="mb-1.5 block">Özel Kısaltma (opsiyonel)</Label><div className="flex items-center gap-1"><span className="text-sm text-slate-400 font-mono">kisalink.net/</span><Input data-testid="link-alias-input" value={form.custom_alias} onChange={(e) => setForm({ ...form, custom_alias: e.target.value })} placeholder="ozelisim" /></div></div>}
            <div><Label className="mb-1.5 block">Başlık</Label><Input data-testid="link-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div><Label className="mb-1.5 block">Özel Görsel</Label><ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="link-image" /></div>
            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-3 text-slate-600">Sosyal Medya / OG Bilgileri</p>
              <div className="space-y-3">
                <Input placeholder="OG Title" value={form.og_title} onChange={(e) => setForm({ ...form, og_title: e.target.value })} data-testid="link-og-title" />
                <Textarea placeholder="OG Description" rows={2} value={form.og_description} onChange={(e) => setForm({ ...form, og_description: e.target.value })} />
                <div><Label className="mb-1.5 block text-xs text-slate-500">OG Image</Label><ImageUpload value={form.og_image} onChange={(v) => setForm({ ...form, og_image: v })} testid="link-og-image" label="OG Görsel" /></div>
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-4">
              <p className="font-medium text-sm">Aktif</p>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="link-active-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={saveLink} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="link-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mini site create dialog */}
      <Dialog open={siteDialog} onOpenChange={setSiteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Yeni Mini Site</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">Sayfa Başlığı</Label><Input data-testid="minisite-title-input" value={newSite.title} onChange={(e) => setNewSite({ ...newSite, title: e.target.value })} placeholder="Örn: Profilim" /></div>
            <div>
              <Label className="mb-1.5 block">Oluşturma Yöntemi</Label>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setNewSite({ ...newSite, mode: "template" })} className={`p-4 rounded-xl border text-left ${newSite.mode === "template" ? "border-blue-600 bg-blue-50" : "border-slate-200"}`} data-testid="mode-template">
                  <LayoutTemplate className="w-5 h-5 text-blue-600" /><p className="font-medium text-sm mt-2">Hazır Şablon</p><p className="text-xs text-slate-400">Bio-link sayfası</p>
                </button>
                <button onClick={() => setNewSite({ ...newSite, mode: "html" })} className={`p-4 rounded-xl border text-left ${newSite.mode === "html" ? "border-blue-600 bg-blue-50" : "border-slate-200"}`} data-testid="mode-html">
                  <ExternalLink className="w-5 h-5 text-blue-600" /><p className="font-medium text-sm mt-2">HTML Editör</p><p className="text-xs text-slate-400">Kendi kodunuz</p>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSiteDialog(false)}>İptal</Button>
            <Button onClick={createSite} className="bg-blue-600 hover:bg-blue-700" data-testid="minisite-create-confirm">Oluştur ve Düzenle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
